import React, { useState, useEffect } from 'react';
import {
  Card,
  Layout,
  TextStyle,
  Stack,
  Badge,
  Button,
  ProgressBar
} from '@shopify/polaris';
import ServerHealthService from '../../services/serverHealthService';

export default function ServerStatusPage() {
  const [mainServerHealth, setMainServerHealth] = useState(null);
  const [imageServerHealth, setImageServerHealth] = useState(null);
  const [stripeServerHealth, setStripeServerHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchServerStatus = async () => {
    setLoading(true);
    try {
      const [mainHealth, imageHealth, stripeHealth] = await Promise.all([
        ServerHealthService.getMainServerHealth(),
        ServerHealthService.getImageServerHealth(),
        ServerHealthService.getStripeServerHealth()
      ]);
      setMainServerHealth(mainHealth);
      setImageServerHealth(imageHealth);
      setStripeServerHealth(stripeHealth);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching server status:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchServerStatus();
    // Refresh every 30 seconds
    const interval = setInterval(fetchServerStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const renderStatusBadge = (status) => {
    const color = status === 'healthy' ? 'success' : 'critical';
    return <Badge status={color}>{status}</Badge>;
  };

  return (
    <Layout>
      <Layout.Section>
        <Card
          title="Main Server Status"
          sectioned
          actions={[
            {
              content: 'Refresh',
              onAction: fetchServerStatus,
              loading: loading
            },
          ]}
        >
          <Stack vertical spacing="loose">
            <Stack distribution="equalSpacing">
              <TextStyle variation="strong">Status</TextStyle>
              {mainServerHealth && renderStatusBadge(mainServerHealth.status)}
            </Stack>
            
            {mainServerHealth && (
              <>
                <Stack distribution="equalSpacing">
                  <TextStyle variation="strong">Environment</TextStyle>
                  <TextStyle>{mainServerHealth.environment}</TextStyle>
                </Stack>

                <Stack distribution="equalSpacing">
                  <TextStyle variation="strong">Version</TextStyle>
                  <TextStyle>{mainServerHealth.version}</TextStyle>
                </Stack>

                {mainServerHealth.server && (
                  <>
                    <Stack distribution="equalSpacing">
                      <TextStyle variation="strong">Uptime</TextStyle>
                      <TextStyle>
                        {ServerHealthService.formatUptime(mainServerHealth.server.uptime)}
                      </TextStyle>
                    </Stack>

                    <Stack distribution="equalSpacing">
                      <TextStyle variation="strong">Node Version</TextStyle>
                      <TextStyle>{mainServerHealth.server.nodeVersion}</TextStyle>
                    </Stack>

                    <Stack distribution="equalSpacing">
                      <TextStyle variation="strong">Server Port</TextStyle>
                      <TextStyle>{mainServerHealth.server.port || 'Not specified'}</TextStyle>
                    </Stack>

                    <Stack distribution="equalSpacing">
                      <TextStyle variation="strong">Server URL</TextStyle>
                      <TextStyle>{mainServerHealth.server.serverUrl || 'Not specified'}</TextStyle>
                    </Stack>

                    {mainServerHealth.config && (
                      <>
                        <Stack distribution="equalSpacing">
                          <TextStyle variation="strong">Domain</TextStyle>
                          <TextStyle>{mainServerHealth.config.domain}</TextStyle>
                        </Stack>
                        <Stack distribution="equalSpacing">
                          <TextStyle variation="strong">Protocol</TextStyle>
                          <TextStyle>{mainServerHealth.config.protocol}</TextStyle>
                        </Stack>
                      </>
                    )}

                    {mainServerHealth.server.memory && (
                      <>
                        <TextStyle variation="strong">Memory Usage</TextStyle>
                        <Stack vertical spacing="extraTight">
                          <Stack.Item>
                            <TextStyle>RSS Memory</TextStyle>
                            <ProgressBar
                              progress={(mainServerHealth.server.memory.rss / (500 * 1024 * 1024)) * 100}
                              size="small"
                              color="primary"
                            />
                            <TextStyle variation="subdued">
                              {ServerHealthService.formatMemory(mainServerHealth.server.memory.rss)}
                            </TextStyle>
                          </Stack.Item>

                          <Stack.Item>
                            <TextStyle>Heap Memory</TextStyle>
                            <ProgressBar
                              progress={(mainServerHealth.server.memory.heapUsed / mainServerHealth.server.memory.heapTotal) * 100}
                              size="small"
                              color="primary"
                            />
                            <Stack distribution="equalSpacing">
                              <TextStyle variation="subdued">
                                Used: {ServerHealthService.formatMemory(mainServerHealth.server.memory.heapUsed)}
                              </TextStyle>
                              <TextStyle variation="subdued">
                                Total: {ServerHealthService.formatMemory(mainServerHealth.server.memory.heapTotal)}
                              </TextStyle>
                            </Stack>
                          </Stack.Item>

                          <Stack.Item>
                            <TextStyle>External Memory</TextStyle>
                            <TextStyle variation="subdued">
                              {ServerHealthService.formatMemory(mainServerHealth.server.memory.external)}
                            </TextStyle>
                          </Stack.Item>
                        </Stack>
                      </>
                    )}
                  </>
                )}

                {mainServerHealth.services && (
                  <>
                    <TextStyle variation="strong">Connected Services</TextStyle>
                    <Stack vertical spacing="extraTight">
                      {mainServerHealth.services.imageProcessing && (
                        <Stack distribution="equalSpacing">
                          <TextStyle>Image Processing</TextStyle>
                          <TextStyle variation="subdued">{mainServerHealth.services.imageProcessing.url}</TextStyle>
                        </Stack>
                      )}
                      {mainServerHealth.services.frontend && (
                        <Stack distribution="equalSpacing">
                          <TextStyle>Frontend</TextStyle>
                          <TextStyle variation="subdued">{mainServerHealth.services.frontend.url}</TextStyle>
                        </Stack>
                      )}
                    </Stack>
                  </>
                )}
              </>
            )}
          </Stack>
        </Card>
      </Layout.Section>

      <Layout.Section>
        <Card
          title="Image Processing Server Status"
          sectioned
          actions={[
            {
              content: 'Refresh',
              onAction: fetchServerStatus,
              loading: loading
            },
          ]}
        >
          <Stack vertical spacing="loose">
            <Stack distribution="equalSpacing">
              <TextStyle variation="strong">Status</TextStyle>
              {imageServerHealth && renderStatusBadge(imageServerHealth.status)}
            </Stack>

            {imageServerHealth && (
              <>
                <Stack distribution="equalSpacing">
                  <TextStyle variation="strong">Environment</TextStyle>
                  <TextStyle>{imageServerHealth.environment}</TextStyle>
                </Stack>

                <Stack distribution="equalSpacing">
                  <TextStyle variation="strong">Version</TextStyle>
                  <TextStyle>{imageServerHealth.version}</TextStyle>
                </Stack>

                <Stack distribution="equalSpacing">
                  <TextStyle variation="strong">Port</TextStyle>
                  <TextStyle>{imageServerHealth.port}</TextStyle>
                </Stack>

                <Stack distribution="equalSpacing">
                  <TextStyle variation="strong">Base URL</TextStyle>
                  <TextStyle>{imageServerHealth.baseUrl}</TextStyle>
                </Stack>

                {imageServerHealth.server && (
                  <>
                    <Stack distribution="equalSpacing">
                      <TextStyle variation="strong">Uptime</TextStyle>
                      <TextStyle>
                        {ServerHealthService.formatUptime(imageServerHealth.server.uptime)}
                      </TextStyle>
                    </Stack>

                    <Stack distribution="equalSpacing">
                      <TextStyle variation="strong">Node Version</TextStyle>
                      <TextStyle>{imageServerHealth.server.nodeVersion}</TextStyle>
                    </Stack>

                    <Stack distribution="equalSpacing">
                      <TextStyle variation="strong">Server Port</TextStyle>
                      <TextStyle>{imageServerHealth.server.port || 'Not specified'}</TextStyle>
                    </Stack>

                    <Stack distribution="equalSpacing">
                      <TextStyle variation="strong">Server URL</TextStyle>
                      <TextStyle>{imageServerHealth.server.serverUrl || 'Not specified'}</TextStyle>
                    </Stack>

                    {imageServerHealth.config && (
                      <>
                        <Stack distribution="equalSpacing">
                          <TextStyle variation="strong">Domain</TextStyle>
                          <TextStyle>{imageServerHealth.config.domain}</TextStyle>
                        </Stack>
                        <Stack distribution="equalSpacing">
                          <TextStyle variation="strong">Protocol</TextStyle>
                          <TextStyle>{imageServerHealth.config.protocol}</TextStyle>
                        </Stack>
                      </>
                    )}

                    {imageServerHealth.server.memory && (
                      <>
                        <TextStyle variation="strong">Memory Usage</TextStyle>
                        <Stack vertical spacing="extraTight">
                          <Stack.Item>
                            <TextStyle>RSS Memory</TextStyle>
                            <ProgressBar
                              progress={(imageServerHealth.server.memory.rss / (500 * 1024 * 1024)) * 100}
                              size="small"
                              color="primary"
                            />
                            <TextStyle variation="subdued">
                              {ServerHealthService.formatMemory(imageServerHealth.server.memory.rss)}
                            </TextStyle>
                          </Stack.Item>

                          <Stack.Item>
                            <TextStyle>Heap Memory</TextStyle>
                            <ProgressBar
                              progress={(imageServerHealth.server.memory.heapUsed / imageServerHealth.server.memory.heapTotal) * 100}
                              size="small"
                              color="primary"
                            />
                            <Stack distribution="equalSpacing">
                              <TextStyle variation="subdued">
                                Used: {ServerHealthService.formatMemory(imageServerHealth.server.memory.heapUsed)}
                              </TextStyle>
                              <TextStyle variation="subdued">
                                Total: {ServerHealthService.formatMemory(imageServerHealth.server.memory.heapTotal)}
                              </TextStyle>
                            </Stack>
                          </Stack.Item>

                          <Stack.Item>
                            <TextStyle>External Memory</TextStyle>
                            <TextStyle variation="subdued">
                              {ServerHealthService.formatMemory(imageServerHealth.server.memory.external)}
                            </TextStyle>
                          </Stack.Item>
                        </Stack>
                      </>
                    )}
                  </>
                )}

                {imageServerHealth.services && (
                  <>
                    <TextStyle variation="strong">Services</TextStyle>
                    <Stack vertical spacing="extraTight">
                      {imageServerHealth.services.storage && (
                        <Stack vertical spacing="extraTight">
                          <TextStyle>Storage</TextStyle>
                          <Stack distribution="equalSpacing">
                            <TextStyle variation="subdued">Type</TextStyle>
                            <TextStyle>{imageServerHealth.services.storage.type}</TextStyle>
                          </Stack>
                          <Stack distribution="equalSpacing">
                            <TextStyle variation="subdued">Path</TextStyle>
                            <TextStyle>{imageServerHealth.services.storage.path}</TextStyle>
                          </Stack>
                          <Stack distribution="equalSpacing">
                            <TextStyle variation="subdued">Status</TextStyle>
                            <Badge status={imageServerHealth.services.storage.available ? 'success' : 'critical'}>
                              {imageServerHealth.services.storage.available ? 'Available' : 'Unavailable'}
                            </Badge>
                          </Stack>
                        </Stack>
                      )}

                      {imageServerHealth.services.imageProcessing && (
                        <Stack vertical spacing="extraTight">
                          <TextStyle>Image Processing</TextStyle>
                          <Stack distribution="equalSpacing">
                            <TextStyle variation="subdued">Sharp Version</TextStyle>
                            <TextStyle>{imageServerHealth.services.imageProcessing.sharp.version}</TextStyle>
                          </Stack>
                          <Stack distribution="equalSpacing">
                            <TextStyle variation="subdued">Status</TextStyle>
                            <Badge status={imageServerHealth.services.imageProcessing.sharp.available ? 'success' : 'critical'}>
                              {imageServerHealth.services.imageProcessing.sharp.available ? 'Available' : 'Unavailable'}
                            </Badge>
                          </Stack>
                          <Stack distribution="equalSpacing">
                            <TextStyle variation="subdued">Supported Formats</TextStyle>
                            <Stack spacing="tight">
                              {imageServerHealth.services.imageProcessing.formats.map(format => (
                                <Badge key={format} status="info">{format}</Badge>
                              ))}
                            </Stack>
                          </Stack>
                        </Stack>
                      )}
                    </Stack>
                  </>
                )}
              </>
            )}
          </Stack>
        </Card>
      </Layout.Section>

      <Layout.Section>
        <Card
          title="Stripe Server Status"
          sectioned
          actions={[
            {
              content: 'Refresh',
              onAction: fetchServerStatus,
              loading: loading
            },
          ]}
        >
          <Stack vertical spacing="loose">
            <Stack distribution="equalSpacing">
              <TextStyle variation="strong">Status</TextStyle>
              {stripeServerHealth && renderStatusBadge(stripeServerHealth.status)}
            </Stack>

            {stripeServerHealth && (
              <>
                <Stack distribution="equalSpacing">
                  <TextStyle variation="strong">Environment</TextStyle>
                  <TextStyle>{stripeServerHealth.environment}</TextStyle>
                </Stack>

                <Stack distribution="equalSpacing">
                  <TextStyle variation="strong">Version</TextStyle>
                  <TextStyle>{stripeServerHealth.version}</TextStyle>
                </Stack>

                {stripeServerHealth.server && (
                  <>
                    <Stack distribution="equalSpacing">
                      <TextStyle variation="strong">Uptime</TextStyle>
                      <TextStyle>
                        {ServerHealthService.formatUptime(stripeServerHealth.server.uptime)}
                      </TextStyle>
                    </Stack>

                    <Stack distribution="equalSpacing">
                      <TextStyle variation="strong">Node Version</TextStyle>
                      <TextStyle>{stripeServerHealth.server.nodeVersion}</TextStyle>
                    </Stack>

                    <Stack distribution="equalSpacing">
                      <TextStyle variation="strong">Server Port</TextStyle>
                      <TextStyle>{stripeServerHealth.server.port || 'Not specified'}</TextStyle>
                    </Stack>

                    <Stack distribution="equalSpacing">
                      <TextStyle variation="strong">Server URL</TextStyle>
                      <TextStyle>{stripeServerHealth.server.serverUrl || 'Not specified'}</TextStyle>
                    </Stack>

                    {stripeServerHealth.config && (
                      <>
                        <Stack distribution="equalSpacing">
                          <TextStyle variation="strong">Domain</TextStyle>
                          <TextStyle>{stripeServerHealth.config.domain}</TextStyle>
                        </Stack>
                        <Stack distribution="equalSpacing">
                          <TextStyle variation="strong">Protocol</TextStyle>
                          <TextStyle>{stripeServerHealth.config.protocol}</TextStyle>
                        </Stack>
                      </>
                    )}

                    {stripeServerHealth.server.memory && (
                      <>
                        <TextStyle variation="strong">Memory Usage</TextStyle>
                        <Stack vertical spacing="extraTight">
                          <Stack.Item>
                            <TextStyle>RSS Memory</TextStyle>
                            <ProgressBar
                              progress={(stripeServerHealth.server.memory.rss / (500 * 1024 * 1024)) * 100}
                              size="small"
                              color="primary"
                            />
                            <TextStyle variation="subdued">
                              {ServerHealthService.formatMemory(stripeServerHealth.server.memory.rss)}
                            </TextStyle>
                          </Stack.Item>

                          <Stack.Item>
                            <TextStyle>Heap Memory</TextStyle>
                            <ProgressBar
                              progress={(stripeServerHealth.server.memory.heapUsed / stripeServerHealth.server.memory.heapTotal) * 100}
                              size="small"
                              color="primary"
                            />
                            <Stack distribution="equalSpacing">
                              <TextStyle variation="subdued">
                                Used: {ServerHealthService.formatMemory(stripeServerHealth.server.memory.heapUsed)}
                              </TextStyle>
                              <TextStyle variation="subdued">
                                Total: {ServerHealthService.formatMemory(stripeServerHealth.server.memory.heapTotal)}
                              </TextStyle>
                            </Stack>
                          </Stack.Item>

                          <Stack.Item>
                            <TextStyle>External Memory</TextStyle>
                            <TextStyle variation="subdued">
                              {ServerHealthService.formatMemory(stripeServerHealth.server.memory.external)}
                            </TextStyle>
                          </Stack.Item>
                        </Stack>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </Stack>
        </Card>
      </Layout.Section>

      {lastUpdated && (
        <Layout.Section>
          <TextStyle variation="subdued">
            Last updated: {lastUpdated.toLocaleTimeString()}
          </TextStyle>
        </Layout.Section>
      )}
    </Layout>
  );
}
